import { useState, FormEvent, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { PasswordField, FormError, SubmitButton } from '@/components/auth/FormFields';
import { resetPassword, readApiError } from '@/lib/auth-api';

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) setError(t('auth.error_missing_token'));
  }, [token, t]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError(t('auth.error_password_too_short'));
      return;
    }
    if (password !== confirm) {
      setError(t('auth.error_passwords_dont_match'));
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      setError(readApiError(err, t('auth.error_reset_failed')));
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <AuthLayout
        title={t('auth.reset_success_title')}
        subtitle={t('auth.reset_success_subtitle')}
      >
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-5 flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
          <p className="text-sm text-emerald-900">{t('auth.reset_redirecting')}</p>
        </div>
      </AuthLayout>
    );
  }

  if (!token) {
    return (
      <AuthLayout
        title={t('auth.reset_invalid_title')}
        subtitle={t('auth.reset_invalid_subtitle')}
        footer={
          <Link to="/forgot-password" className="font-semibold text-accent-700 hover:underline">
            {t('auth.request_new_link')}
          </Link>
        }
      >
        <div className="rounded-2xl bg-warn-50 border border-warn-500/40 px-4 py-5 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-warn-600 mt-0.5 shrink-0" />
          <p className="text-sm text-warn-600">{t('auth.error_missing_token')}</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.reset_title')} subtitle={t('auth.reset_subtitle')}>
      <form className="space-y-4" onSubmit={submit} noValidate>
        <PasswordField
          label={t('auth.new_password')}
          autoComplete="new-password"
          required
          showStrength
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint={t('auth.password_hint')}
        />
        <PasswordField
          label={t('auth.confirm_password')}
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        <FormError message={error} />
        <SubmitButton loading={loading}>{t('auth.reset_submit')}</SubmitButton>
      </form>
    </AuthLayout>
  );
}
